/**
 * Pin-to-bottom for a chat scroll container.
 *
 * This is the ONLY thing that moves a chat viewport. MessageList used to run a
 * second scroll of its own, keyed on the visible message count; the two fought
 * over the same scrollable element, because a count-keyed smooth scroll targets
 * the same ancestor this hook welds with a direct `scrollTop` assignment, and
 * each assignment aborted the animation mid-glide. It was also blind to
 * everything that grows the conversation without changing the count:
 *
 *  - a turn.delta stream growing ONE bubble (count unchanged),
 *  - an async turn landing via the events/poll path (count changes, but the
 *    markdown, tables and citations lay out after the scroll already ran),
 *  - the onboarding confirm card, a widget, or a late-loading font.
 *
 * So this observes the CONTENT BOXES rather than trying to enumerate the things
 * that change them. Anything that makes the conversation taller re-pins, and the
 * hook cannot fall behind a growth path nobody thought to pass in.
 *
 * The pin stands down the moment the user scrolls up — reading back through a
 * conversation must never be yanked away, which is why `isPinned` is also
 * returned, for a jump-to-latest affordance.
 *
 * WHAT "THE USER SCROLLED UP" MEANS, AND WHY IT IS NOT GEOMETRY. This hook used
 * to decide with one measurement: `scrollHeight - scrollTop - clientHeight <
 * 100px` meant anchored, anything else meant the user had scrolled away. That
 * expression cannot tell a nurse dragging the conversation up apart from the
 * page changing shape underneath her, and BOTH of the other two terms move on
 * their own:
 *
 *  - `scrollHeight` jumps the instant a tall block lands — a dosing table, a
 *    citation list, a widget. The ResizeObserver re-pins a moment later, but
 *    any `scroll` event delivered in between (iOS Safari scrolls off the main
 *    thread, so its events carry a position the main thread has not caught up
 *    with) is measured against the ALREADY-grown height and reads as a 300px
 *    scroll-up that never happened.
 *  - `clientHeight` collapses when the iOS keyboard opens — several hundred
 *    pixels, no scroll, no growth, nobody's finger anywhere near the page.
 *
 * Either one detached the pin, and because a detached pin makes the
 * ResizeObserver bail, it stayed detached for the rest of the turn: the answer
 * streamed on off-screen and the "Latest" pill appeared over a conversation the
 * nurse had never scrolled. The one thing neither can do is move `scrollTop`
 * against the user's wishes, so `scrollTop` moving UP — measured against a
 * high-water mark, so a slow drag accumulates instead of being read as jitter —
 * is what stands the pin down now. Growth and keyboards are silent to it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '@/components/ui/animation-tokens';

/**
 * How far above the bottom the user must have travelled for the pin to stand
 * down, in px — and, symmetrically, how close she must come back for it to
 * re-arm. Hysteresis on one number: a nudge that lands inside it is not a
 * decision to read back, and it also absorbs the sub-pixel offsets fractional
 * device-pixel ratios leave in `scrollHeight - scrollTop - clientHeight`.
 */
const PIN_RELEASE_DISTANCE_PX = 24;

/**
 * Upward `scrollTop` movement, in px, below which we assume browser rounding
 * rather than intent. Measured against the high-water mark rather than the
 * previous event, so a slow deliberate drag accumulates past it instead of
 * being dismissed one sub-threshold step at a time.
 */
const USER_SCROLL_TOLERANCE_PX = 4;

export interface PinToBottom<T extends HTMLElement> {
  /** Attach to the scrolling element. */
  containerRef: React.RefObject<T | null>;
  /**
   * True while the viewport is following the newest content. Goes false only
   * when the user scrolls up herself — use it to reveal a jump-to-latest
   * control.
   */
  isPinned: boolean;
  /** Jump to the newest content, and follow it again — for that control. */
  scrollToBottom: () => void;
}

export function usePinToBottom<T extends HTMLElement>(): PinToBottom<T> {
  const containerRef = useRef<T>(null);
  const [isPinned, setIsPinnedState] = useState(true);
  // The scroll/resize callbacks are registered once and would otherwise close
  // over the first render's value forever.
  const isPinnedRef = useRef(true);
  /**
   * High-water mark of `scrollTop` while pinned: the furthest down the user has
   * been. Content growing below her raises `scrollHeight` without touching
   * `scrollTop`, so the mark holds still and nothing reads as movement; only
   * she can push `scrollTop` below it.
   */
  const bottomAnchorRef = useRef(0);

  const setPinned = useCallback((next: boolean) => {
    isPinnedRef.current = next;
    // Bail on an unchanged value: scroll fires per frame, and re-setting the
    // same boolean would re-render the whole conversation on every one.
    setIsPinnedState((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      // Clamped: a conversation shorter than its viewport has no scroll range,
      // and a negative distance (iOS rubber-band overscroll) is still "here".
      const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
      const distanceFromBottom = Math.max(0, maxScrollTop - scrollTop);

      if (isPinnedRef.current) {
        // Re-clamp the mark to what is currently scrollable before comparing:
        // when content SHRINKS (turn.failed drops a placeholder) the browser
        // drags scrollTop down with it, and a stale mark would read that as
        // the user scrolling up.
        const anchor = Math.min(
          Math.max(bottomAnchorRef.current, scrollTop),
          maxScrollTop,
        );
        bottomAnchorRef.current = anchor;
        const movedUp = scrollTop < anchor - USER_SCROLL_TOLERANCE_PX;
        if (movedUp && distanceFromBottom > PIN_RELEASE_DISTANCE_PX) {
          setPinned(false);
        }
        return;
      }

      // Detached: the user came back to the bottom under her own steam, so
      // follow the conversation again.
      if (distanceFromBottom <= PIN_RELEASE_DISTANCE_PX) {
        bottomAnchorRef.current = scrollTop;
        setPinned(true);
      }
    };
    // passive: scroll handlers must never preventDefault — the hint lets the
    // browser skip its "will this block scrolling?" check.
    el.addEventListener('scroll', onScroll, { passive: true });

    const observer = new ResizeObserver(() => {
      if (!isPinnedRef.current) return;
      // Assign rather than scrollTo({behavior:'smooth'}): while tokens stream
      // in, a smooth animation is restarted by each growth and never lands, so
      // the view trails the text. Direct assignment keeps the bottom edge
      // welded to the viewport.
      el.scrollTop = el.scrollHeight;
      // Carry the mark with us. The `scroll` event this assignment queues is
      // delivered later — possibly after further growth — and would otherwise
      // be compared against where the viewport was before the jump.
      bottomAnchorRef.current = el.scrollTop;
    });

    // Observe the children, not the scroller: the scroller's own box is fixed
    // by the layout, only its contents grow. EVERY child, not just the first —
    // Session.tsx keeps the composer inside the same scroller, and a textarea
    // growing to three lines pushes the newest answer out of view exactly like
    // a table does.
    const observeChildren = () => {
      observer.disconnect();
      const children = Array.from(el.children);
      if (children.length === 0) {
        observer.observe(el);
        return;
      }
      for (const child of children) observer.observe(child);
    };
    observeChildren();
    // Children come and go (a dismissible degraded-service banner, a
    // conditional card): an observer wired once at mount goes blind to
    // whatever mounts after it.
    const childObserver = new MutationObserver(observeChildren);
    childObserver.observe(el, { childList: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      childObserver.disconnect();
      observer.disconnect();
    };
  }, [setPinned]);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // Re-arm BEFORE the glide starts, so content landing mid-animation is
    // followed rather than ignored. The mark stays at the current position and
    // rises with the animation — seeding it with the destination instead would
    // make every frame of the glide look like a scroll-up.
    bottomAnchorRef.current = el.scrollTop;
    setPinned(true);
    el.scrollTo({
      top: el.scrollHeight,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, [setPinned]);

  return { containerRef, isPinned, scrollToBottom };
}
